import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useCurrentUser, useIsLoggedIn } from '../context/hooks';
import { Button, Select, Col, Form, Input, message, Modal, Row, Typography } from 'antd';
import { CallbackEvent, MediaType, RoomClient } from '../modules/mediasoup.module';
import { SocketModule } from '../modules/Socket.module';
import { useQuery, useMutation } from '@tanstack/react-query';

const Mediasoup = () => {
  const history = useHistory();
  const currentUser = useCurrentUser();

  //#region media devices
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string | undefined>();
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState<string | undefined>();
  const getMediaDevices = useCallback(() => {
    navigator.mediaDevices
      .getUserMedia({
        audio: true,
        video: true,
      })
      .then((stream) => {
        // Load mediaDevice options
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          console.log('Devices: ', devices);
          setAudioDevices(devices.filter((device) => device.kind === 'audioinput'));
          setVideoDevices(devices.filter((device) => device.kind === 'videoinput'));
          // device.kind: 'audioinput' | 'audiooutput' | 'videoinput'
          // device.deviceId
          // device.label
        });

        stream.getTracks().forEach(function (track) {
          track.stop();
        });
      })
      .catch((err) => {
        console.error('Access denied for audio/video: ', err);
      });
  }, []);
  //#endregion

  //#region join room
  const [isJoined, setIsJoined] = useState(false);
  const [room, setRoom] = useState('test');

  const handleJoinRoom = useCallback(async () => {
    if (room.trim() === '') {
      message.error('Please enter a room name');
      return;
    }
    await roomClient.current.joinRoom(room);
    setIsJoined(true);
    console.log('Joined room:', await roomClient.current.getRoomDetails());
  }, [room]);

  const handleExitRoom = useCallback(() => {
    roomClient.current.exit();
  }, []);
  //#endregion

  //#region produce
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);

  const { isPending: isStartingAudio, mutate: startAudio } = useMutation({
    mutationFn: async () => roomClient.current.produce({ requestId: 'room-audio', type: MediaType.audio, deviceId: selectedAudioDeviceId }),
    onError: (error) => {
      console.error('Failed to start audio:', error);
    },
    onSuccess: () => {
      setAudioEnabled(true);
    },
  });

  const { isPending: isStoppingAudio, mutate: stopAudio } = useMutation({
    mutationFn: async () => roomClient.current.closeProducer('room-audio'),
    onError: (error) => {
      console.error('Failed to stop audio:', error);
    },
    onSuccess: () => {
      setAudioEnabled(false);
    },
  });

  const { isPending: isStartingVideo, mutate: startVideo } = useMutation({
    mutationFn: async () => roomClient.current.produce({ requestId: 'room-video', type: MediaType.video, deviceId: selectedVideoDeviceId }),
    onError: (error) => {
      console.error('Failed to start video:', error);
    },
    onSuccess: () => {
      setVideoEnabled(true);
    },
  });

  const { isPending: isStoppingVideo, mutate: stopVideo } = useMutation({
    mutationFn: async () => roomClient.current.closeProducer('room-video'),
    onError: (error) => {
      console.error('Failed to stop video:', error);
    },
    onSuccess: () => {
      setVideoEnabled(false);
    },
  });

  //#endregion

  //#region streams
  const [renderKey, setRenderKey] = useState(0);
  const localAudioStreamRef = useRef<MediaStream | null>(null);
  const localVideoStreamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioStreamsRef = useRef<{ [consumerId: string]: MediaStream }>({});
  const remoteVideoStreamsRef = useRef<{ [consumerId: string]: MediaStream }>({});
  const remoteAudioRefs = useRef<{ [consumerId: string]: HTMLAudioElement | null }>({});
  const remoteVideoRefs = useRef<{ [consumerId: string]: HTMLVideoElement | null }>({});

  const [isMuted, setIsMuted] = useState(false);
  const toggleMute = useCallback(() => {
    const newMuteState = !isMuted;
    localAudioStreamRef.current?.getAudioTracks().forEach(track => {
      track.enabled = !newMuteState;
    });
    setIsMuted(newMuteState);
  }, [isMuted]);

  //#endregion

  //#region callbacks
  const handleCallbacks = useCallback((event: CallbackEvent, data?: any) => {
    console.log('Mediasoup callback event:', event, data);
    switch (event) {
      case CallbackEvent.exitRoom:
        setIsJoined(false);
        setAudioEnabled(false);
        setVideoEnabled(false);
        setTargetUserEnabled(false);
        setIsMuted(false);
        console.log('Exited room');
        break;
      case CallbackEvent.startAudio: {
           const { stream } = data as {
            stream: MediaStream;
          };
          localAudioStreamRef.current = stream;
          console.log('Audio started');
      }
        break;
      case CallbackEvent.stopAudio: {
          localAudioStreamRef.current?.getTracks().forEach(track => {
            track.stop();
          });
          localAudioStreamRef.current = null;
          console.log('Audio stopped');
      }
        break;
      case CallbackEvent.startVideo:
        {
          const { stream } = data as { stream: MediaStream; };
          localVideoStreamRef.current = stream;
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
          console.log('Video started');
        }
        break;
      case CallbackEvent.stopVideo:
        {
          localVideoStreamRef.current?.getTracks().forEach((track) => {
            track.stop();
          });
          localVideoStreamRef.current = null;
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = null;
          }
          console.log('Video stopped');
        }
        break;
      case CallbackEvent.startConsumer:
        {
          const { stream, consumer, kind } = data as { stream: MediaStream; consumer: any; kind: 'audio' | 'video' };
          if (kind === 'video') {
            remoteVideoStreamsRef.current[consumer.id] = stream;
          }
          if (kind === 'audio') {
            remoteAudioStreamsRef.current[consumer.id] = stream;
          }
          console.log('Consuming media from user:', consumer.appData?.producerUserId);
          if (consumer.appData?.producerUserId) {
            // TODO handle producer user ID (e.g., show user info)
          }
          setRenderKey(prev => prev + 1); // trigger re-render
          console.log('Remote Media started', kind, consumer.id);
        }
        break;
      case CallbackEvent.stopConsumer:
        {
          const { kind, consumerId } = data as { kind: 'audio' | 'video', consumerId: string };
          if (kind === 'video') {
             remoteVideoStreamsRef.current[consumerId]?.getTracks().forEach(track => {
              track.stop();
            });
            delete remoteVideoStreamsRef.current[consumerId];
          }
          if ((kind === 'audio')) {
            remoteAudioStreamsRef.current[consumerId]?.getTracks().forEach(track => {
              track.stop();
            });
            delete remoteAudioStreamsRef.current[consumerId];
          }
          setRenderKey(prev => prev + 1); // trigger re-render
          console.log('Remote Media stopped', kind);
        }
        break;
      default:
        break;
    }
  }, []);

  const roomClient = useRef<RoomClient>(new RoomClient(SocketModule.emit, SocketModule.on, SocketModule.off, handleCallbacks));
  //#endregion

  //#region routing

  const [targetUserEnabled, setTargetUserEnabled] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  
  const startTargetUser = useCallback(() => {
    if (targetUserId.trim() === '') {
      message.error('Please enter a target user ID');
      return;
    }
    roomClient.current.produce({ requestId: 'target-video', type: MediaType.video, deviceId: selectedVideoDeviceId, appData: { targetUserIds: [targetUserId.trim()] } })
    setTargetUserEnabled(true);
  }, [targetUserId]);

  const stopTargetUser = useCallback(() => {
    roomClient.current.closeProducer('target-video')
    setTargetUserEnabled(false);
  }, []);

  //#endregion

  return (
    <div className="p-32">
      <div className="mt-24">
        User ID: <strong>{currentUser?._id}</strong>
      </div>
      <div className="mt-24">
        <Input value={room} onChange={(e) => setRoom(e.target.value)} placeholder="Room" />
      </div>
      <div className="mt-24">{isJoined ? <Button onClick={handleExitRoom}>Exit room</Button> : <Button onClick={handleJoinRoom}>Join room</Button>}</div>

      <div className="mt-24">
        <Button onClick={getMediaDevices}>Get Media Devices</Button>
      </div>
      <div className="mt-24 flex flex-row space-x-24">
        <Select className="w-1/2 border border-gray" placeholder="Audio devices" value={selectedAudioDeviceId} onSelect={setSelectedAudioDeviceId}>
          {audioDevices.map((device) => (
            <Select.Option key={`${device.kind}-${device.deviceId}`} value={device.deviceId}>
              {device.label || device.deviceId}
            </Select.Option>
          ))}
        </Select>
        <Select className="w-1/2 border border-gray" placeholder="Video devices" value={selectedVideoDeviceId} onSelect={setSelectedVideoDeviceId}>
          {videoDevices.map((device) => (
            <Select.Option key={`${device.kind}-${device.deviceId}`} value={device.deviceId}>
              {device.label || device.deviceId}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="mt-24 space-x-24">
        {audioEnabled ? <Button loading={isStoppingAudio} onClick={() => stopAudio()}>Stop audio</Button> : <Button loading={isStartingAudio} onClick={() => startAudio()}>Start audio</Button>}
        {videoEnabled ? <Button loading={isStoppingVideo} onClick={() => stopVideo()}>Stop video</Button> : <Button loading={isStartingVideo} onClick={() => startVideo()}>Start video</Button>}
        <Button onClick={toggleMute}>{isMuted ? 'Unmute' : 'Mute'}</Button>

        <Input value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} placeholder="Target User ID" className="w-300" />
        {targetUserEnabled ? <Button onClick={stopTargetUser}>Stop target user</Button> : <Button onClick={startTargetUser}>Start target user</Button>}
      </div>

      <h3 className="mt-24">Local Media</h3>
      <div className="mt-24">
        <video ref={localVideoRef} autoPlay playsInline muted style={{ width: '500px' }} />
      </div>

      <h3 className="mt-24">Remote Media</h3>
      <div key={'video'+renderKey} className="mt-24 flex flex-row space-x-24">
        {Object.entries(remoteVideoStreamsRef.current).map(([consumerId, stream]) => (
            <video
              key={'video'+consumerId}
              ref={(el) => {
                remoteVideoRefs.current[consumerId] = el;
                if (el) {
                  el.srcObject = stream;
                }
              }}
              autoPlay
              playsInline
              muted
              style={{ width: 500, height: 300, marginRight: 24, background: '#000', border: '2px solid red' }}
            />
        ))}
      </div>

      <div key={'audio'+renderKey}>
        {Object.entries(remoteAudioStreamsRef.current).map(([consumerId, stream]) => (
            <audio
              key={'audio'+consumerId}
              ref={(el) => {
                remoteAudioRefs.current[consumerId] = el;
                if (el) {
                  el.srcObject = stream;
                }
              }}
              autoPlay
              playsInline={false}
            />
        ))}
      </div>
    </div>
  );
};

export default React.memo(Mediasoup);
